import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Star, Users, Sparkles, LogOut, Flame, Award, 
  ChevronRight, Calendar, HelpCircle, FolderOpen, Target, 
  Briefcase, CheckCircle, Brain, MapPin, User, ArrowLeft, Heart
} from 'lucide-react';

// Daydream Theme - Friendly, pastel, approachable
// Colors: Soft Pink background (#FFF1F2), Rose primary (#FB7185), Coral (#FDA4AF), Pink accent (#F472B6)
// Font: Fredoka (headings), Quicksand (body)
// Style: Very rounded (2rem), diffuse soft shadows, bubbly feel

const DaydreamPreview = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pb-24" style={{ 
      backgroundColor: '#FFF1F2',
      fontFamily: "'Quicksand', sans-serif",
      color: '#4C0519'
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 bg-white rounded-full p-2.5"
        style={{ boxShadow: '0 4px 15px rgba(251, 113, 133, 0.3)' }}
      >
        <ArrowLeft className="w-5 h-5 text-rose-400" />
      </button>

      {/* Theme Label */}
      <div 
        className="fixed top-4 right-4 z-50 bg-white text-rose-500 px-4 py-2 rounded-full font-bold text-sm"
        style={{ boxShadow: '0 4px 20px rgba(251, 113, 133, 0.3)' }}
      >
        Theme 4: Daydream
      </div>

      {/* Hero Header */}
      <div className="relative overflow-hidden">
        {/* Gradient background */}
        <div 
          className="absolute inset-0" 
          style={{ 
            background: 'linear-gradient(135deg, #FB7185 0%, #F472B6 50%, #FDA4AF 100%)'
          }} 
        />
        
        {/* Floating bubbles */}
        <div className="absolute top-10 right-10 w-20 h-20 rounded-full bg-white/20 blur-sm" />
        <div className="absolute top-32 right-32 w-12 h-12 rounded-full bg-white/25" />
        <div className="absolute bottom-20 left-10 w-16 h-16 rounded-full bg-white/15 blur-sm" />
        <div className="absolute top-20 left-20 w-8 h-8 rounded-full bg-white/30" />
        
        <div className="relative px-4 pt-16 pb-10">
          <div className="max-w-md mx-auto">
            {/* Logo Bar */}
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center"
                  style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}
                >
                  <Heart className="w-6 h-6 text-rose-400" fill="currentColor" />
                </div>
                <div className="text-white">
                  <p className="text-xs font-medium opacity-80">Newark SYEP</p>
                  <p className="text-sm font-bold">Summer 2026</p>
                </div>
              </div>
              <button className="bg-white/20 backdrop-blur rounded-full p-3">
                <LogOut className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Welcome Section */}
            <div className="text-center mb-8">
              <div 
                className="w-24 h-24 mx-auto rounded-full bg-white flex items-center justify-center mb-4"
                style={{ boxShadow: '0 8px 30px rgba(251, 113, 133, 0.4)' }}
              >
                <span className="text-4xl font-bold text-rose-400" style={{ fontFamily: "'Fredoka', sans-serif" }}>B</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Hi Brandon! ✨
              </h1>
              <p className="text-white/80 text-sm font-medium">Ready for another amazing day?</p>
              <div className="flex justify-center gap-2 mt-3">
                <span className="bg-white/30 text-white text-xs font-bold px-4 py-1.5 rounded-full backdrop-blur">
                  UpNEXT SYEP
                </span>
              </div>
            </div>

            {/* Worksite Info */}
            <div 
              className="bg-white/20 backdrop-blur rounded-3xl p-4 mb-6"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div className="text-white">
                  <p className="text-xs font-medium opacity-70">Your Worksite</p>
                  <p className="font-bold">Newark Public Library</p>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Points Card */}
              <div 
                className="bg-white rounded-3xl p-5"
                style={{ boxShadow: '0 8px 30px rgba(251, 113, 133, 0.2)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Star className="w-4 h-4 text-amber-500" fill="currentColor" />
                  </div>
                  <span className="text-xs font-bold text-rose-300 uppercase">Points</span>
                </div>
                <p className="text-3xl font-bold text-rose-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  1,250
                </p>
              </div>

              {/* Streak Card */}
              <div 
                className="bg-white rounded-3xl p-5"
                style={{ boxShadow: '0 8px 30px rgba(251, 113, 133, 0.2)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Flame className="w-4 h-4 text-orange-500" />
                  </div>
                  <span className="text-xs font-bold text-rose-300 uppercase">Streak</span>
                </div>
                <p className="text-3xl font-bold text-rose-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  7 <span className="text-lg font-normal text-rose-300">days</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Curved bottom */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{ 
            background: '#FFF1F2',
            borderRadius: '100% 100% 0 0'
          }}
        />
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-4 space-y-6">
        
        {/* Quick Actions */}
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
          {[
            { icon: Target, label: 'Goals', bg: 'bg-emerald-100', color: 'text-emerald-500' },
            { icon: Briefcase, label: 'Project', bg: 'bg-orange-100', color: 'text-orange-500' },
            { icon: Trophy, label: 'Rankings', bg: 'bg-amber-100', color: 'text-amber-500' },
            { icon: Award, label: 'Wins', bg: 'bg-pink-100', color: 'text-pink-500' },
            { icon: FolderOpen, label: 'Files', bg: 'bg-violet-100', color: 'text-violet-500' },
            { icon: HelpCircle, label: 'Help', bg: 'bg-sky-100', color: 'text-sky-500' },
          ].map((item) => (
            <button
              key={item.label}
              className="flex-shrink-0 bg-white rounded-3xl p-4 min-w-[85px] transition-transform hover:scale-105"
              style={{ boxShadow: '0 4px 15px rgba(251, 113, 133, 0.15)' }}
            >
              <div className={`w-10 h-10 ${item.bg} rounded-2xl mx-auto mb-2 flex items-center justify-center`}>
                <item.icon className={`w-5 h-5 ${item.color}`} />
              </div>
              <p className="text-xs font-bold text-rose-400 text-center">{item.label}</p>
            </button>
          ))}
        </div>

        {/* Work Journal Card */}
        <div 
          className="bg-white rounded-[2rem] p-6 relative overflow-hidden"
          style={{ boxShadow: '0 8px 30px rgba(251, 113, 133, 0.2)' }}
        >
          <div className="absolute top-[-30px] right-[-30px] w-24 h-24 rounded-full bg-rose-100/50" />
          <div className="absolute bottom-[-20px] left-[-20px] w-16 h-16 rounded-full bg-pink-100/50" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-400 rounded-2xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-rose-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  Work Journal
                </h2>
                <p className="text-rose-300 text-sm font-medium">Share your weekly moments</p>
              </div>
            </div>
            <button 
              className="w-full bg-gradient-to-r from-rose-400 to-pink-400 text-white rounded-full py-4 font-bold text-lg hover:from-rose-500 hover:to-pink-500 transition-all"
              style={{ fontFamily: "'Fredoka', sans-serif", boxShadow: '0 4px 15px rgba(251, 113, 133, 0.4)' }}
            >
              Drop a Win! +50 pts
            </button>
          </div>
        </div>

        {/* This Week Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-rose-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-4 h-4 text-rose-400" />
            </div>
            <h2 className="text-lg font-bold text-rose-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              This Week
            </h2>
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            <div 
              className="bg-gradient-to-r from-violet-400 to-purple-400 rounded-3xl p-5 cursor-pointer"
              style={{ boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Skills Check</h3>
                    <p className="text-white/80 text-sm">See how you've grown • 50 pts</p>
                  </div>
                </div>
                <span className="bg-white text-violet-500 text-xs font-bold px-3 py-1.5 rounded-full">
                  New!
                </span>
              </div>
            </div>

            {/* Check-in Card */}
            <div 
              className="bg-white rounded-3xl p-4 cursor-pointer"
              style={{ boxShadow: '0 4px 15px rgba(251, 113, 133, 0.15)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-rose-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>Weekly Check-in</h3>
                    <p className="text-rose-300 text-sm">How's your week? • 20 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-rose-300" />
              </div>
            </div>

            {/* Trivia Card */}
            <div 
              className="bg-white rounded-3xl p-4 cursor-pointer"
              style={{ boxShadow: '0 4px 15px rgba(251, 113, 133, 0.15)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-rose-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>Fun Trivia</h3>
                    <p className="text-rose-300 text-sm">Play and learn • 25 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-rose-300" />
              </div>
            </div>
          </div>
        </div>

        {/* Team Card */}
        <div 
          className="bg-white rounded-[2rem] p-5 relative overflow-hidden"
          style={{ boxShadow: '0 8px 30px rgba(251, 113, 133, 0.2)' }}
        >
          <div className="absolute top-0 right-0 w-20 h-20 rounded-full bg-pink-100/50 blur-xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-gradient-to-br from-pink-400 to-rose-400 rounded-2xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-rose-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>Team Alpha</h3>
                  <p className="text-rose-300 text-sm">8 awesome members</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-rose-500" style={{ fontFamily: "'Fredoka', sans-serif" }}>4,500</p>
                <p className="text-rose-300 text-xs">team pts</p>
              </div>
            </div>
            <button 
              className="w-full bg-rose-100 hover:bg-rose-200 rounded-full py-3 text-rose-500 font-bold transition-colors"
              style={{ fontFamily: "'Fredoka', sans-serif" }}
            >
              See Your Team <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-rose-100 px-4 py-2"
      >
        <div className="max-w-md mx-auto flex justify-around items-center">
          {[
            { icon: '🏠', label: 'Home', active: true },
            { icon: '🎯', label: 'Goals', active: false },
            { icon: '📊', label: 'Progress', active: false },
            { icon: '👤', label: 'Profile', active: false },
          ].map((item) => (
            <button 
              key={item.label} 
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-2xl transition-all ${
                item.active 
                  ? 'bg-rose-100 text-rose-500' 
                  : 'text-rose-300 hover:text-rose-400'
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="text-xs font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DaydreamPreview;
